import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../../../services/api";
import { buildPublicFileUrl } from "../../../config/env";

// MUI
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Typography,
  Grid,
  Stack,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonIcon from "@mui/icons-material/Person";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// Colors (kept)
const COLORS = {
  accent: "#768f3a",
  primary: "#1e3e7b",
  secondary: "#2788b9",
  light: "#f0f2f5",
  white: "#ffffff",
};

/**
 * Return the MUI chip color for a given asset status.
 */
function getAssetStatusColor(status) {
  switch ((status || "").toLowerCase()) {
    case "assigned":
      return "success";
    case "returned":
      return "secondary";
    case "maintenance":
      return "warning";
    case "available":
      return "default";
    default:
      return "default";
  }
}

/** Generic status chip for user */
function getUserStatusColor(status) {
  switch ((status || "").toLowerCase()) {
    case "active":
      return "success";
    case "inactive":
      return "default";
    case "suspended":
      return "warning";
    default:
      return "default";
  }
}

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

function formatDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getInitials(name) {
  const n = (name || "").trim();
  if (!n) return "U";
  const parts = n.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join("");
}

function safeArray(v) {
  if (Array.isArray(v)) return v;
  if (Array.isArray(v?.data)) return v.data;
  return [];
}

/** Small info tile */
function InfoTile({ label, value, mono = false, onCopy }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.25,
        borderRadius: 2.5,
        border: (t) => `1px solid ${alpha(t.palette.divider, 0.14)}`,
        bgcolor: (t) =>
          t.palette.mode === "light"
            ? alpha(t.palette.background.default, 0.6)
            : alpha(t.palette.background.default, 0.25),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
            {label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 950,
              mt: 0.25,
              fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" : "inherit",
              wordBreak: "break-word",
            }}
          >
            {value ?? "—"}
          </Typography>
        </Box>

        {onCopy && value ? (
          <Tooltip title="Copy">
            <IconButton size="small" onClick={() => onCopy(String(value))} sx={{ borderRadius: 2 }}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    </Paper>
  );
}

function SectionCard({ title, subtitle, right, children }) {
  return (
    <Card variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: (t) =>
            t.palette.mode === "light"
              ? alpha(t.palette.primary.main, 0.04)
              : alpha(t.palette.primary.main, 0.10),
          borderBottom: (t) => `1px solid ${alpha(t.palette.divider, 0.12)}`,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {right ? <Box>{right}</Box> : null}
        </Stack>
      </Box>
      <CardContent sx={{ p: 2 }}>{children}</CardContent>
    </Card>
  );
}

export default function ShowUserPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  /* ------------ State ------------ */
  const [user, setUser] = useState(null);

  const [assets, setAssets] = useState([]);
  const [family, setFamily] = useState([]);
  const [policies, setPolicies] = useState([]); // insurance policies
  const [certificates, setCertificates] = useState([]);

  // Loading states
  const [loadingU, setLoadingU] = useState(true);
  const [loadingA, setLoadingA] = useState(true);
  const [loadingF, setLoadingF] = useState(true);
  const [loadingP, setLoadingP] = useState(true);
  const [loadingC, setLoadingC] = useState(true);

  // Error states (optional but useful)
  const [errAssets, setErrAssets] = useState("");
  const [errFamily, setErrFamily] = useState("");
  const [errPolicies, setErrPolicies] = useState("");
  const [errCerts, setErrCerts] = useState("");

  // Tabs: 0=Profile, 1=Assets, 2=Family, 3=Insurance, 4=Certificates
  const [tab, setTab] = useState(0);

  // Filters
  const [assetFilter, setAssetFilter] = useState({
    q: "",
    status: "all",
    type: "all",
    condition: "all",
  });

  const [familyFilter, setFamilyFilter] = useState({
    q: "",
    relationship: "all",
  });

  const [certFilter, setCertFilter] = useState({
    q: "",
    year: "all",
  });

  /* ------------ Copy helper ------------ */
  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
  };

  /* ------------ Fetch User ------------ */
  const fetchUser = useCallback(async () => {
    setLoadingU(true);
    try {
      const { data } = await api.get(`/admin/users/${id}`);
      setUser(data);
    } catch (error) {
      console.error("Error fetching user:", error);
      setUser(null);
    } finally {
      setLoadingU(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  /* ------------ Fetch Related ------------ */
  useEffect(() => {
    if (!user?.employee_code) return;

    // Assets
    (async () => {
      setLoadingA(true);
      setErrAssets("");
      try {
        const { data } = await api.get(`/assets/employee/${user.employee_code}`);
        setAssets(safeArray(data));
      } catch (error) {
        console.error("Error fetching assets:", error);
        setErrAssets("Failed to load assets.");
        setAssets([]);
      } finally {
        setLoadingA(false);
      }
    })();

    // Family
    (async () => {
      setLoadingF(true);
      setErrFamily("");
      try {
        const { data } = await api.get(`/admin/users/${id}/family-members`);
        setFamily(safeArray(data));
      } catch (error) {
        console.error("Error fetching family:", error);
        setErrFamily("Failed to load family members.");
        setFamily([]);
      } finally {
        setLoadingF(false);
      }
    })();

    // Certificates
    (async () => {
      setLoadingC(true);
      setErrCerts("");
      try {
        const { data } = await api.get(`/admin/users/${id}/certificates`);
        setCertificates(safeArray(data));
      } catch (error) {
        console.error("Error fetching certificates:", error);
        setErrCerts("Failed to load certificates.");
        setCertificates([]);
      } finally {
        setLoadingC(false);
      }
    })();

    // Insurance policies
    (async () => {
      setLoadingP(true);
      setErrPolicies("");
      try {
        const { data } = await api.get(`/admin/users/${id}/assigned-policies`);
        const list = safeArray(data);
        setPolicies(list);
      } catch (error) {
        console.error("Error fetching insurance:", error);
        setErrPolicies("Failed to load insurance policies.");
        setPolicies([]);
      } finally {
        setLoadingP(false);
      }
    })();
  }, [id, user?.employee_code]);

  /* ------------ Tab Handler ------------ */
  const handleTabChange = (_, newValue) => setTab(newValue);

  /* ------------ Navigate Back ------------ */
  const handleBack = () => navigate(-1);

  const handleRefreshAll = async () => {
    await fetchUser(); // other data will refresh via useEffect (employee_code)
  };

  /* ------------ Summary counts ------------ */
  const counts = useMemo(
    () => ({
      assets: assets?.length || 0,
      family: family?.length || 0,
      policies: policies?.length || 0,
      certificates: certificates?.length || 0,
    }),
    [assets, family, policies, certificates]
  );

  /* ------------ Asset filter options ------------ */
  const assetTypeOptions = useMemo(() => {
    const s = new Set((assets || []).map((a) => String(a.asset_type || "").trim()).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [assets]);

  const assetConditionOptions = useMemo(() => {
    const s = new Set((assets || []).map((a) => String(a.condition || "").trim()).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [assets]);

  const filteredAssets = useMemo(() => {
    let list = [...(assets || [])];

    const q = assetFilter.q.trim().toLowerCase();
    if (q) {
      list = list.filter((a) => {
        const hay = [
          a.asset_tag,
          a.asset_type,
          a.serial_number,
          a.condition,
          a.status,
          a.asset_tag?.toString(),
        ]
          .map((v) => (v == null ? "" : String(v).toLowerCase()))
          .join(" ");
        return hay.includes(q);
      });
    }

    if (assetFilter.status !== "all") {
      list = list.filter((a) => String(a.status || "").toLowerCase() === assetFilter.status);
    }

    if (assetFilter.type !== "all") {
      list = list.filter((a) => String(a.asset_type || "").toLowerCase() === assetFilter.type);
    }

    if (assetFilter.condition !== "all") {
      list = list.filter((a) => String(a.condition || "").toLowerCase() === assetFilter.condition);
    }

    // sort newest first by updated/assignment if available
    list.sort((x, y) => {
      const xv = new Date(x.updated_at || x.assignment_date || 0).getTime() || 0;
      const yv = new Date(y.updated_at || y.assignment_date || 0).getTime() || 0;
      return yv - xv;
    });

    return list;
  }, [assets, assetFilter]);

  /* ------------ Family filters ------------ */
  const relationshipOptions = useMemo(() => {
    const s = new Set((family || []).map((m) => String(m.relationship || "").trim()).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [family]);

  const filteredFamily = useMemo(() => {
    let list = [...(family || [])];

    const q = familyFilter.q.trim().toLowerCase();
    if (q) {
      list = list.filter((m) => {
        const hay = [m.name, m.relationship, m.birth_date, m.policy_name, m.insurance_classification]
          .map((v) => (v == null ? "" : String(v).toLowerCase()))
          .join(" ");
        return hay.includes(q);
      });
    }

    if (familyFilter.relationship !== "all") {
      list = list.filter(
        (m) => String(m.relationship || "").toLowerCase() === familyFilter.relationship
      );
    }

    return list;
  }, [family, familyFilter]);

  /* ------------ Certificates filters ------------ */
  const certYearOptions = useMemo(() => {
    const years = new Set(
      (certificates || [])
        .map((c) => {
          const d = c.issue_date || c.created_at;
          if (!d) return null;
          const dt = new Date(d);
          if (Number.isNaN(dt.getTime())) return null;
          return String(dt.getFullYear());
        })
        .filter(Boolean)
    );
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  }, [certificates]);

  const filteredCertificates = useMemo(() => {
    let list = [...(certificates || [])];

    const q = certFilter.q.trim().toLowerCase();
    if (q) {
      list = list.filter((c) => {
        const hay = [c.title, c.issue_date, c.expiry_date, c.issuer, c.file_path]
          .map((v) => (v == null ? "" : String(v).toLowerCase()))
          .join(" ");
        return hay.includes(q);
      });
    }

    if (certFilter.year !== "all") {
      list = list.filter((c) => {
        const d = c.issue_date || c.created_at;
        const dt = d ? new Date(d) : null;
        if (!dt || Number.isNaN(dt.getTime())) return false;
        return String(dt.getFullYear()) === certFilter.year;
      });
    }

    list.sort((x, y) => {
      const xv = new Date(x.issue_date || x.created_at || 0).getTime() || 0;
      const yv = new Date(y.issue_date || y.created_at || 0).getTime() || 0;
      return yv - xv;
    });

    return list;
  }, [certificates, certFilter]);

  /* ------------ Guards ------------ */
  if (loadingU) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 5 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading user…
        </Typography>
      </Box>
    );
  }

  if (!user) {
    return (
      <Typography variant="h6" align="center" sx={{ mt: 5 }}>
        User not found.
      </Typography>
    );
  }

  const dept = user?.departmentObj?.name || user?.departmentObj?.code || user?.department || "—";
  const managerName = user?.manager?.name || (user?.manager_id ? `Code: ${user.manager_id}` : "—");

  const profileImageSrc = buildPublicFileUrl(user?.profile_image_url || user?.profile_image_path);
  const passportImageSrc = buildPublicFileUrl(user?.passport_image_url || user?.passport_image_path);

  const tabLabels = [
    `Profile`,
    `Assets (${counts.assets})`,
    `Family (${counts.family})`,
    `Insurance (${counts.policies})`,
    `Certificates (${counts.certificates})`,
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ maxWidth: 1150, margin: "auto", padding: "1rem" }}
    >
      {/* Header (More informative) */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
          background:
            theme.palette.mode === "light"
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(
                  theme.palette.background.paper,
                  0.95
                )} 60%)`
              : alpha(theme.palette.background.paper, 0.9),
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: "primary.main",
                fontWeight: 950,
              }}
              src={profileImageSrc || undefined}
            >
              {!profileImageSrc ? getInitials(user.name) : null}
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 980, color: COLORS.primary, lineHeight: 1.1 }} noWrap>
                {user.name || "—"}
              </Typography>

              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.3 }} noWrap>
                Code: <b>{user.employee_code || "—"}</b> • {dept}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                <Chip size="small" label={`Status: ${user.status || "—"}`} color={getUserStatusColor(user.status)} />
                <Chip size="small" label={`System Role: ${user.role || "—"}`} variant="outlined" />
                <Chip size="small" label={`Appraisal Role: ${user.actual_role || "—"}`} variant="outlined" />
                {user.project_id ? <Chip size="small" label={`Project ID: ${user.project_id}`} variant="outlined" /> : null}
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
            <Tooltip title="Refresh">
              <IconButton
                onClick={handleRefreshAll}
                sx={{ borderRadius: 2.5, border: `1px solid ${alpha(theme.palette.divider, 0.18)}` }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ borderRadius: 2.5, fontWeight: 900 }}>
              Back
            </Button>

            <Button
              component={RouterLink}
              to={`/admin/users/edit/${user.id}`}
              variant="contained"
              startIcon={<EditIcon />}
              sx={{ borderRadius: 2.5, fontWeight: 900, textTransform: "none" }}
            >
              Edit User
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Quick KPI row (click to switch tab) */}
        <Grid container spacing={1.5}>
          {[
            { label: "Assets", value: counts.assets, tabIndex: 1, icon: <Inventory2Icon fontSize="small" /> },
            { label: "Family Members", value: counts.family, tabIndex: 2, icon: <FamilyRestroomIcon fontSize="small" /> },
            { label: "Insurance Policies", value: counts.policies, tabIndex: 3, icon: <HealthAndSafetyIcon fontSize="small" /> },
            { label: "Certificates", value: counts.certificates, tabIndex: 4, icon: <WorkspacePremiumIcon fontSize="small" /> },
          ].map((k) => (
            <Grid item xs={6} md={3} key={k.label}>
              <Paper
                elevation={0}
                onClick={() => setTab(k.tabIndex)}
                sx={{
                  p: 1.25,
                  borderRadius: 2.5,
                  border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
                  cursor: "pointer",
                  transition: "transform 140ms ease, box-shadow 140ms ease",
                  "&:hover": { transform: "translateY(-1px)", boxShadow: theme.shadows[2] },
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: alpha(theme.palette.primary.main, 0.10),
                      color: "primary.main",
                    }}
                  >
                    {k.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                      {k.label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 980, lineHeight: 1.1 }}>
                      {k.value}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.14)}` }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            px: 1,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
            "& .MuiTab-root": { fontWeight: 900, textTransform: "none" },
          }}
        >
          <Tab icon={<PersonIcon />} iconPosition="start" label={tabLabels[0]} />
          <Tab icon={<Inventory2Icon />} iconPosition="start" label={tabLabels[1]} />
          <Tab icon={<FamilyRestroomIcon />} iconPosition="start" label={tabLabels[2]} />
          <Tab icon={<HealthAndSafetyIcon />} iconPosition="start" label={tabLabels[3]} />
          <Tab icon={<WorkspacePremiumIcon />} iconPosition="start" label={tabLabels[4]} />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {/* ---------- Profile (Tab 0) ---------- */}
          {tab === 0 && (
            <Stack spacing={2.5}>
              <SectionCard
                title="Profile Information"
                subtitle="Personal + work details"
                right={
                  <Chip
                    size="small"
                    label={`Updated: ${formatDate(user.updated_at)}`}
                    variant="outlined"
                    sx={{ fontWeight: 800 }}
                  />
                }
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Name" value={user.name} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Employee Code" value={user.employee_code} mono onCopy={copyText} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <InfoTile label="Email" value={user.email || "—"} onCopy={copyText} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="System Role" value={user.role || "—"} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <InfoTile label="Appraisal Role" value={user.actual_role || "—"} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Status" value={user.status || "—"} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <InfoTile label="Department" value={dept} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Position" value={user.position || "—"} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <InfoTile label="Cost Center" value={user.cost_center || "—"} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Site" value={user.site || "—"} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <InfoTile label="Nationality" value={user.nationality || "—"} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Birth Date" value={formatDate(user.birth_date)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Contract Type" value={user.contract_type || "—"} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <InfoTile label="Iqama No." value={user.iqama_no || "—"} mono onCopy={copyText} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Manager" value={managerName} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <InfoTile label="Created At" value={formatDateTime(user.created_at)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Updated At" value={formatDateTime(user.updated_at)} />
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard title="Passport & Contract" subtitle="Optional documents and dates">
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Passport No." value={user.passport_no || "-"} mono onCopy={copyText} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Passport Issued Date" value={formatDate(user.passport_issue_date)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Passport Expiry Date" value={formatDate(user.passport_expiry_date)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Contract Issued Date" value={formatDate(user.contract_issue_date)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Contract Expiry Date" value={formatDate(user.contract_expiry_date)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoTile label="Profile Image" value={profileImageSrc ? "Uploaded" : "Not uploaded"} />
                  </Grid>
                </Grid>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }}>
                  {profileImageSrc ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      href={profileImageSrc}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}
                    >
                      Open Profile Image
                    </Button>
                  ) : null}

                  {passportImageSrc ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      href={passportImageSrc}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}
                    >
                      Open Passport Image
                    </Button>
                  ) : null}
                </Stack>
              </SectionCard>

              <SectionCard title="Bank & Payroll" subtitle="Banking details (if provided)">
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    <InfoTile label="Bank Name" value={user.bank_name || "—"} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InfoTile label="IBAN" value={user.iban || "—"} mono onCopy={copyText} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InfoTile label="Account Number" value={user.account_number || "—"} mono onCopy={copyText} />
                  </Grid>
                </Grid>
              </SectionCard>
            </Stack>
          )}

          {/* ---------- Assets (Tab 1) ---------- */}
          {tab === 1 && (
            <SectionCard
              title="Assigned Assets"
              subtitle={`Showing ${filteredAssets.length} of ${assets.length}`}
              right={
                <Button
                  size="small"
                  startIcon={<RestartAltIcon />}
                  variant="outlined"
                  sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}
                  onClick={() =>
                    setAssetFilter({ q: "", status: "all", type: "all", condition: "all" })
                  }
                >
                  Clear
                </Button>
              }
            >
              {/* Filters */}
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} md={5}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Search assets"
                    placeholder="Tag, serial, type, condition..."
                    value={assetFilter.q}
                    onChange={(e) => setAssetFilter((p) => ({ ...p, q: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2.33}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={assetFilter.status}
                      onChange={(e) => setAssetFilter((p) => ({ ...p, status: e.target.value }))}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="assigned">assigned</MenuItem>
                      <MenuItem value="returned">returned</MenuItem>
                      <MenuItem value="maintenance">maintenance</MenuItem>
                      <MenuItem value="available">available</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2.33}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={assetFilter.type}
                      onChange={(e) => setAssetFilter((p) => ({ ...p, type: e.target.value }))}
                    >
                      <MenuItem value="all">All</MenuItem>
                      {assetTypeOptions.map((t) => (
                        <MenuItem key={t} value={t.toLowerCase()}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2.33}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Condition</InputLabel>
                    <Select
                      label="Condition"
                      value={assetFilter.condition}
                      onChange={(e) =>
                        setAssetFilter((p) => ({ ...p, condition: e.target.value }))
                      }
                    >
                      <MenuItem value="all">All</MenuItem>
                      {assetConditionOptions.map((c) => (
                        <MenuItem key={c} value={c.toLowerCase()}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Content */}
              {loadingA ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading Assets…
                  </Typography>
                </Box>
              ) : errAssets ? (
                <Alert severity="error">{errAssets}</Alert>
              ) : assets.length === 0 ? (
                <Typography>No assets assigned.</Typography>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead
                      sx={{
                        backgroundColor: theme.palette.mode === "light" ? "#f7f7f7" : alpha("#fff", 0.06),
                        "& th": { fontWeight: 900 },
                      }}
                    >
                      <TableRow>
                        <TableCell>Tag</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Serial #</TableCell>
                        <TableCell>Condition</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Assigned</TableCell>
                        <TableCell>Return</TableCell>
                        <TableCell align="right">Open</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAssets.map((asset) => (
                        <TableRow key={asset.id} hover>
                          <TableCell sx={{ fontWeight: 900 }}>{asset.asset_tag || "—"}</TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {asset.asset_type || "—"}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "ui-monospace, Menlo, Monaco, Consolas, monospace" }}>
                            {asset.serial_number || "—"}
                          </TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {asset.condition || "—"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={asset.status || "—"}
                              color={getAssetStatusColor(asset.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatDate(asset.assignment_date)}</TableCell>
                          <TableCell>{formatDate(asset.return_date)}</TableCell>
                          <TableCell align="right">
                            <Button
                              component={RouterLink}
                              to={`/assets/${asset.id}`}
                              variant="outlined"
                              size="small"
                              startIcon={<OpenInNewIcon />}
                              sx={{ borderRadius: 2.25, textTransform: "none", fontWeight: 900 }}
                            >
                              Open
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredAssets.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8}>
                            <Typography textAlign="center" sx={{ py: 2 }}>
                              No assets match your filters.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>
          )}

          {/* ---------- Family (Tab 2) ---------- */}
          {tab === 2 && (
            <SectionCard
              title="Family Members"
              subtitle={`Showing ${filteredFamily.length} of ${family.length}`}
              right={
                <Button
                  size="small"
                  startIcon={<RestartAltIcon />}
                  variant="outlined"
                  sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}
                  onClick={() => setFamilyFilter({ q: "", relationship: "all" })}
                >
                  Clear
                </Button>
              }
            >
              {/* Filters */}
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} md={8}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Search family"
                    placeholder="Name, relationship, policy..."
                    value={familyFilter.q}
                    onChange={(e) => setFamilyFilter((p) => ({ ...p, q: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Relationship</InputLabel>
                    <Select
                      label="Relationship"
                      value={familyFilter.relationship}
                      onChange={(e) =>
                        setFamilyFilter((p) => ({ ...p, relationship: e.target.value }))
                      }
                    >
                      <MenuItem value="all">All</MenuItem>
                      {relationshipOptions.map((r) => (
                        <MenuItem key={r} value={r.toLowerCase()}>
                          {r}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {loadingF ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading Family Members…
                  </Typography>
                </Box>
              ) : errFamily ? (
                <Alert severity="error">{errFamily}</Alert>
              ) : family.length === 0 ? (
                <Typography>No family members recorded.</Typography>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead
                      sx={{
                        backgroundColor: theme.palette.mode === "light" ? "#f7f7f7" : alpha("#fff", 0.06),
                        "& th": { fontWeight: 900 },
                      }}
                    >
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Relationship</TableCell>
                        <TableCell>Birth</TableCell>
                        <TableCell>Policy</TableCell>
                        <TableCell>Class</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredFamily.map((member) => (
                        <TableRow key={member.id} hover>
                          <TableCell sx={{ fontWeight: 900 }}>{member.name || "—"}</TableCell>
                          <TableCell>{member.relationship || "—"}</TableCell>
                          <TableCell>{member.birth_date || "—"}</TableCell>
                          <TableCell>{member.policy_name || "—"}</TableCell>
                          <TableCell>{member.insurance_classification || "—"}</TableCell>
                        </TableRow>
                      ))}
                      {filteredFamily.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography textAlign="center" sx={{ py: 2 }}>
                              No family members match your filters.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>
          )}

          {/* ---------- Insurance (Tab 3) ---------- */}
          {tab === 3 && (
            <SectionCard
              title="Insurance Policies"
              subtitle={`Assigned policies: ${policies.length}`}
              right={
                <Chip
                  size="small"
                  variant="outlined"
                  label={policies.length ? "Has Insurance" : "No Insurance"}
                  color={policies.length ? "success" : "default"}
                  sx={{ fontWeight: 900 }}
                />
              }
            >
              {loadingP ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading Insurance…
                  </Typography>
                </Box>
              ) : errPolicies ? (
                <Alert severity="error">{errPolicies}</Alert>
              ) : policies.length === 0 ? (
                <Typography>No active insurance assigned.</Typography>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead
                      sx={{
                        backgroundColor: theme.palette.mode === "light" ? "#f7f7f7" : alpha("#fff", 0.06),
                        "& th": { fontWeight: 900 },
                      }}
                    >
                      <TableRow>
                        <TableCell>Policy</TableCell>
                        <TableCell>Classification</TableCell>
                        <TableCell>Start</TableCell>
                        <TableCell>End</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {policies.map((p, idx) => (
                        <TableRow key={p.id || idx} hover>
                          <TableCell sx={{ fontWeight: 900 }}>
                            {p.policy_name || p.name || "—"}
                          </TableCell>
                          <TableCell>{p.classification || "—"}</TableCell>
                          <TableCell>{p.start_date || "—"}</TableCell>
                          <TableCell>{p.end_date || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>
          )}

          {/* ---------- Certificates (Tab 4) ---------- */}
          {tab === 4 && (
            <SectionCard
              title="Certificates"
              subtitle={`Showing ${filteredCertificates.length} of ${certificates.length}`}
              right={
                <Button
                  size="small"
                  startIcon={<RestartAltIcon />}
                  variant="outlined"
                  sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 900 }}
                  onClick={() => setCertFilter({ q: "", year: "all" })}
                >
                  Clear
                </Button>
              }
            >
              {/* Filters */}
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} md={8}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Search certificates"
                    placeholder="Title, issuer, date..."
                    value={certFilter.q}
                    onChange={(e) => setCertFilter((p) => ({ ...p, q: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Year</InputLabel>
                    <Select
                      label="Year"
                      value={certFilter.year}
                      onChange={(e) => setCertFilter((p) => ({ ...p, year: e.target.value }))}
                    >
                      <MenuItem value="all">All</MenuItem>
                      {certYearOptions.map((y) => (
                        <MenuItem key={y} value={y}>
                          {y}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {loadingC ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading Certificates…
                  </Typography>
                </Box>
              ) : errCerts ? (
                <Alert severity="error">{errCerts}</Alert>
              ) : certificates.length === 0 ? (
                <Typography>No certificates found.</Typography>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead
                      sx={{
                        backgroundColor: theme.palette.mode === "light" ? "#f7f7f7" : alpha("#fff", 0.06),
                        "& th": { fontWeight: 900 },
                      }}
                    >
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Issue Date</TableCell>
                        <TableCell>Issuer</TableCell>
                        <TableCell>File</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCertificates.map((cert) => {
                        const url = buildPublicFileUrl(cert.file_path);
                        return (
                          <TableRow key={cert.id} hover>
                            <TableCell sx={{ fontWeight: 900 }}>{cert.title || "—"}</TableCell>
                            <TableCell>{cert.issue_date || formatDate(cert.created_at)}</TableCell>
                            <TableCell>{cert.issuer || "—"}</TableCell>
                            <TableCell>
                              {url ? (
                                <a href={url} target="_blank" rel="noreferrer">
                                  View
                                </a>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {filteredCertificates.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Typography textAlign="center" sx={{ py: 2 }}>
                              No certificates match your filters.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>
          )}

          <AdminSessionsSection userId={id} />
        </Box>
      </Paper>
    </motion.div>
  );
}

/**
 * Where this employee is signed in.
 *
 * This is employee monitoring rather than the employee's own account security,
 * so it is loaded on demand rather than with the rest of the page — an admin
 * opening a profile for an unrelated reason should not silently generate an
 * access record. Every fetch is audit-logged server-side.
 */
function AdminSessionsSection({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/admin/users/${userId}/sessions`);
      setData(res.data);
    } catch (err) {
      setError(
        err?.response?.status === 403
          ? "You do not have permission to view sign-in activity."
          : err?.response?.data?.message || "Could not load sign-in activity."
      );
    } finally {
      setLoading(false);
    }
  };

  const when = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <SectionCard
      title="Sign-in Activity"
      subtitle="Devices currently signed in to this account."
      right={
        <Button size="small" variant="outlined" onClick={load} disabled={loading}>
          {data ? "Refresh" : "Load"}
        </Button>
      }
    >
      {error ? <Alert severity="error">{error}</Alert> : null}

      {!data && !loading && !error ? (
        <Typography variant="body2" color="text.secondary">
          Sign-in activity is not loaded by default. Viewing it is recorded in the audit log.
        </Typography>
      ) : null}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={22} />
        </Stack>
      ) : null}

      {data ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Device</TableCell>
                <TableCell>IP address</TableCell>
                <TableCell>Signed in</TableCell>
                <TableCell>Last active</TableCell>
                <TableCell>Second factor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.sessions.map((s) => (
                <TableRow key={s.session_id}>
                  <TableCell>{s.device_label}</TableCell>
                  <TableCell>{s.ip || "—"}</TableCell>
                  <TableCell>{when(s.created_at)}</TableCell>
                  <TableCell>{when(s.last_seen_at)}</TableCell>
                  <TableCell>
                    {s.mfa_verified ? "Code verified" : s.trusted_device ? "Trusted device" : "No"}
                  </TableCell>
                </TableRow>
              ))}
              {data.sessions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography textAlign="center" sx={{ py: 2 }}>
                      No active sessions.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </SectionCard>
  );
}
